// ================================================================
// IMPORTS AND TYPES
// ================================================================

/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import Filters from '@/components/UI/Filters';
import Selector from '@/components/UI/Table/Columns/Selector';
import { TableProvider } from '@/components/UI/Table/TableContext';
import TableWithContext from '@/components/UI/Table/TableWithContext';
import { successToast } from '@/helpers';
import { _isEmpty, _isNotEmpty } from '@/littledash';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import Http from '@/support/http';
import { featuresSelector } from '@/support/Selectors';
import { useUserTableViews } from '@/utils/userTableSettings/UserTableSettingsContext';
import type { AxiosResponse } from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  type SortingState,
  type PaginationState,
  type RowSelectionState,
} from '@tanstack/react-table';

import ActionBar from './ActionBar';
import { ApiInfo, APITableProps } from './APITable.model';
import {
  downloadFile,
  getExportUrl,
  getParams,
  addFilterToFilters,
  shouldPerformInitialSelection,
  shouldNotifyInitialSelection,
  mergeColumnVisibility,
  filterSystemColumnsFromVisibility,
} from './APITable.utils';
import ExportButton from './ExportButton';
import ViewSelector from './ViewSelector/ViewSelector';
import type {
  UserTableViewSettings,
  UserTableViewFilter,
  UserTableViewTableName,
} from '@/model/UserTableView.model.ts';

// ================================================================
// SELECTORS
// ================================================================

const featureSelector = createSelector([featuresSelector], (features) => ({
  userTableViewsEnabled: features?.user_table_views ?? false,
}));

// ================================================================
// CONSTANTS AND UTILITY FUNCTIONS
// ================================================================

/**
 * Invokes fetch table data based on API info type
 */
const invokeFetchTableData = async (
  { type, route }: ApiInfo,
  queryParams: string,
  signal: AbortSignal
): Promise<AxiosResponse<{ data: any; meta: any }>> => {
  switch (type) {
    case 'internalApi':
      return Http.request({ method: 'GET', baseURL: `${route.origin}${route.pathname}${queryParams}`, signal });
    case 'legacyInternalApi':
      return Http.get(`${route}${queryParams}`, { signal });
    default:
      return Promise.reject(new Error(`Unknown API type [${type}]`));
  }
};

// ================================================================
// MAIN API TABLE COMPONENT
// ================================================================

const APITable = <Resp extends object>({
  // Table data configuration
  columns = [],
  filterOptions,
  apiInfo,
  queryParams,
  onFetchTableData = () => {},
  includeParam,
  excludeParam,
  customFilter,
  customFilterProcessor,

  // UI components
  AsideComponent: ExternalAsideComponent,
  NoDataComponent,

  // Table features
  bulkActions,
  exportRoutes,
  renderCustomActions,
  onSelectRow,

  // Search and filtering
  searchPlaceholderText = '',
  hideSearch,
  hideFilter,
  searchQueryByOptions = [],

  // Display options
  loadingText,
  loadingClassName = 'pv7',
  noDataMessage,
  testIdPrefix = '',
  maxHeight,

  // Pagination and sorting
  defaultSortBy = { id: 'created_at', desc: true },
  pageSizes = [10, 25, 50, 1000],
  defaultPageSize,

  // Settings and state
  reduxTableName,
  showMetricChangeFrom = false,
  refetch,
  dataProp = '',
  defaultFilters = [],
  selectAllOnLoad = false,
  showViewSelector = false,
}: APITableProps<Resp>): React.ReactElement => {
  // ================================================================
  // FEATURE FLAGS
  // ================================================================

  const { userTableViewsEnabled } = useSelector(featureSelector);

  // ================================================================
  // STATE MANAGEMENT
  // ================================================================

  // Get user's active view settings (undefined if no active view)
  const { getActiveSettings, isLoaded: settingsLoaded } = useUserTableViews();
  const savedSettings = getActiveSettings(reduxTableName as UserTableViewTableName);

  // Initial state configuration using saved settings or defaults
  const initialTableState = useMemo(
    () => ({
      tableData: [] as Record<string, unknown>[],
      searchQuery: '',
      queryBy: searchQueryByOptions?.[0]?.value,
      filters: (savedSettings?.filters ?? defaultFilters) as unknown as Array<Record<string, unknown>>,
      filterMatch: savedSettings?.match_all_filters ?? true,
      pageCount: 0,
      pageSize: savedSettings?.page_size ?? defaultPageSize ?? pageSizes[0],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Only compute once on mount
  );

  // Table data and search state
  const [tableData, setTableData] = useState<Record<string, unknown>[]>(initialTableState.tableData);
  const [searchQuery, setSearchQuery] = useState(initialTableState.searchQuery);
  const [queryBy, setQueryBy] = useState(initialTableState.queryBy);

  // Filtering state
  const [filters, setFilters] = useState<Record<string, unknown>[]>(initialTableState.filters);
  const [filterMatch, setFilterMatch] = useState(initialTableState.filterMatch);

  // Pagination state
  const [pageCount, setPageCount] = useState(initialTableState.pageCount);
  const [totalItemCount, setTotalItemCount] = useState<number | null>(null);

  // UI state
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [apiError, setApiError] = useState<boolean | unknown>(false);
  const [hasPerformedInitialSelection, setHasPerformedInitialSelection] = useState(false);
  const [needsInitialSelectionNotification, setNeedsInitialSelectionNotification] = useState(false);
  const { newAbortController } = useAbortController();
  const tableRef = useRef<HTMLDivElement>(null);
  const hasSelectorColumn = Boolean(bulkActions || renderCustomActions || onSelectRow);

  // ================================================================
  // TANSTACK TABLE CONFIGURATION
  // ================================================================

  // ================================================================
  // HELPER FUNCTIONS
  // ================================================================

  const resetTableState = () => {
    setPagination({ pageIndex: 0, pageSize: initialTableState.pageSize });
    setSelectAllActive(false);
    setSearchQuery(initialTableState.searchQuery);
    setQueryBy(initialTableState.queryBy);
    setFilters(initialTableState.filters);
    setFilterMatch(initialTableState.filterMatch);
    setPageCount(initialTableState.pageCount);
    setRowSelection({});
  };

  const addFilter = (filter: unknown) => {
    const newFilters = addFilterToFilters(filters as never, filter as never);
    setFilters(newFilters as unknown as Record<string, unknown>[]);
  };

  // ================================================================
  // COLUMN PROCESSING WITH STRICT TYPING
  // ================================================================

  // Columns are already in TanStack Table v8 format
  const memoColumns = useMemo(() => {
    let processedColumns = [...columns] as any[];

    if (hasSelectorColumn) {
      processedColumns = [Selector(false, onSelectRow), ...processedColumns];
    }

    return processedColumns;
  }, [columns, hasSelectorColumn, onSelectRow]);

  // TanStack Table v8 internal state management
  const [sorting, setSorting] = useState<SortingState>(() =>
    savedSettings?.sort ? [savedSettings.sort] : [{ id: defaultSortBy.id, desc: defaultSortBy.desc }]
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialTableState.pageSize,
  });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>(savedSettings?.column_sizing ?? {});

  // Build default column visibility from column definitions (isVisible prop)
  const defaultColumnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    memoColumns.forEach((col) => {
      const columnDef = col as any;
      if (columnDef.id !== 'selection' && columnDef?.isVisible === false) {
        visibility[col.id as string] = false;
      }
    });
    return visibility;
  }, [memoColumns]);

  // Use saved visibility if available, otherwise use defaults from column definitions
  const [columnVisibility, setColumnVisibility] = useState(() =>
    mergeColumnVisibility(savedSettings?.column_visibility, defaultColumnVisibility)
  );

  const handleViewChange = (settings: UserTableViewSettings | undefined) => {
    if (settings) {
      // Apply the view's settings
      setColumnVisibility(mergeColumnVisibility(settings.column_visibility, defaultColumnVisibility));
      setColumnSizing(settings.column_sizing ?? {});
      setPagination((prev) => ({ ...prev, pageIndex: 0, pageSize: settings.page_size }));
      setSorting(settings.sort ? [settings.sort] : [{ id: defaultSortBy.id, desc: defaultSortBy.desc }]);
      setFilters(settings.filters as unknown as Record<string, unknown>[]);
      setFilterMatch(settings.match_all_filters ?? true);
    } else {
      // Reset to defaults ("Default" view selected)
      setColumnVisibility(defaultColumnVisibility);
      setColumnSizing({});
      setPagination((prev) => ({ ...prev, pageIndex: 0, pageSize: defaultPageSize ?? pageSizes[0] }));
      setSorting([{ id: defaultSortBy.id, desc: defaultSortBy.desc }]);
      setFilters(defaultFilters as unknown as Record<string, unknown>[]);
      setFilterMatch(true);
    }
  };

  // Reset row selection when pagination or table data length changes(after delete or bulk delete)
  useEffect(() => {
    setRowSelection({});
  }, [pagination, tableData.length]);

  // TanStack Table v8 configuration
  const table = useReactTable({
    data: tableData,
    columns: memoColumns as any,
    state: {
      sorting,
      pagination,
      rowSelection,
      columnSizing,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableHiding: true,
    columnResizeMode: 'onChange',
  });

  // Extract current state values for backward compatibility
  const { pageIndex, pageSize } = pagination;
  const sortBy = sorting.map((sort) => ({
    id: sort.id,
    desc: sort.desc,
  }));

  // ================================================================
  // API DATA FETCHING
  // ================================================================

  const fetchTableData = async (resetPage: boolean = false) => {
    const params = getParams({
      searchQuery,
      searchQueryBy: queryBy ?? initialTableState.queryBy ?? 'name',
      filters,
      filterMatch,
      sortBy,
      pageSize,
      pageIndex,
      includeParam,
      excludeParam: excludeParam as never,
      resetPage,
      customFilter,
      customFilterProcessor,
      queryParams,
      urlSearchParams: apiInfo.type === 'internalApi' ? (apiInfo.route as URL).searchParams : undefined,
    });
    setUpdating(true);
    try {
      const {
        data: { data, meta, ...otherProps },
      } = await invokeFetchTableData(apiInfo, params, newAbortController().signal);

      setTableData(data ?? []);
      let altData: Record<string, unknown>[] = [];
      if (dataProp) {
        altData = (otherProps as Record<string, unknown>)[dataProp] as Record<string, unknown>[];
        setTableData(altData);
      }
      if (onFetchTableData) {
        onFetchTableData(data, otherProps);
      }
      setPageCount(meta?.pagination?.total_pages ?? meta?.last_page);
      setTotalItemCount(meta?.total ?? null);
      setApiError(false);
      setLoading(false);
      setUpdating(false);
      return data ?? altData;
    } catch (error) {
      if (notAborted(error)) {
        setApiError(error);
        setLoading(false);
        setUpdating(false);
      }
    }
  };

  // Refetch table data without resetting state (pagination, filters preserved, selection cleared)
  const refetchTableData = async () => {
    setSelectAllActive(false);
    setRowSelection({});
    await fetchTableData();
  };

  // Set meta property for TanStack Table v8 cell functions
  // This needs to be done after fetchTableData is defined
  (table.options as any).meta = {
    apiTable: {
      fetchTableData,
      refetchTableData,
      setUpdating,
      setApiError,
      resetTableState,
      addFilter,
    },
  };

  // ================================================================
  // BACKWARD COMPATIBILITY LAYER
  // ================================================================

  // Create backward compatible useTableProps object for v7 compatibility
  const useTableProps = {
    ...table,
    // v7 compatibility methods
    gotoPage: (pageIndex: number) => setPagination((prev) => ({ ...prev, pageIndex })),
    setPageSize: (pageSize: number) => setPagination((prev) => ({ ...prev, pageSize })),
    toggleAllRowsSelected: table.toggleAllRowsSelected,
    state: { pageIndex, pageSize, sortBy },
    selectedFlatRows: table.getSelectedRowModel().flatRows.map((row) => ({
      ...row,
      original: row.original,
      index: row.index,
    })),
    rows: table.getRowModel().rows, // Required by ActionBar
    isAllRowsSelected: table.getIsAllRowsSelected(), // Required by ActionBar

    // UseTableShowMenu compatibility props
    allColumns: table.getAllColumns().map((col) => ({
      id: col.id,
      Header: typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id,
      isVisible: col.getIsVisible(),
    })),
    visibleColumns: table.getVisibleLeafColumns().map((col) => ({
      id: col.id,
      Header: typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id,
    })),
    toggleHideColumn: (columnId: string, hide?: boolean) => {
      const column = table.getColumn(columnId);
      if (column) {
        if (hide !== undefined) {
          column.toggleVisibility(!hide);
        } else {
          column.toggleVisibility();
        }
      }
    },
    toggleHideAllColumns: (hide: boolean) => {
      table.toggleAllColumnsVisible(!hide);
    },

    // Additional props for backward compatibility
    pageCount,
    totalItemCount,
    pageSizes,
    NoDataComponent,
    apiTable: {
      fetchTableData,
      refetchTableData,
      setUpdating,
      setApiError,
      resetTableState,
      addFilter,
    },
    noDataMessage,
  };

  const getTableData = (resetPage?: boolean) => {
    if (!updating) {
      fetchTableData(resetPage || false);
    }
  };

  // ================================================================
  // EFFECT HOOKS
  // ================================================================

  // Track if column visibility has been initialized
  // We only want to set initial visibility once when columns are loaded, not reset it on re-renders
  // User changes to column visibility should persist across re-renders
  const [hasInitializedColumnVisibility, setHasInitializedColumnVisibility] = useState(false);

  useEffect(() => {
    // Only initialize table state when we have actual data columns (not just the selector)
    // and user table settings have loaded (to avoid race condition on login)
    const hasDataColumns = columns.length > 0;
    if (!hasInitializedColumnVisibility && hasDataColumns && settingsLoaded) {
      // Apply saved column visibility
      const newVisibility = mergeColumnVisibility(savedSettings?.column_visibility, defaultColumnVisibility);
      setColumnVisibility(newVisibility);

      // Apply saved sorting, filters, page size, and column sizing
      if (savedSettings) {
        if (savedSettings.sort) {
          setSorting([savedSettings.sort]);
        }
        if (savedSettings.filters) {
          setFilters(savedSettings.filters as unknown as Record<string, unknown>[]);
        }
        setFilterMatch(savedSettings.match_all_filters ?? true);
        if (savedSettings.page_size) {
          setPagination((prev) => ({ ...prev, pageSize: savedSettings.page_size }));
        }
        if (savedSettings.column_sizing) {
          setColumnSizing(savedSettings.column_sizing);
        }
      }

      setHasInitializedColumnVisibility(true);
    }
  }, [defaultColumnVisibility, savedSettings, columns.length, settingsLoaded]);

  // Handle "Select All" functionality with pagination
  const prevTableDataLengthRef = useRef(tableData.length);

  useEffect(() => {
    if (selectAllActive && !updating && tableData.length > 0) {
      // Only toggle if data has actually changed (new data loaded)
      if (prevTableDataLengthRef.current !== tableData.length) {
        table.toggleAllRowsSelected(true);
        prevTableDataLengthRef.current = tableData.length;

        // Only reset selectAllActive if we're at pageSize 1000 (meaning we loaded all data)
        if (pageSize === 1000) {
          setSelectAllActive(false);
        }
      }
    }

    // Update ref when data changes
    if (!selectAllActive) {
      prevTableDataLengthRef.current = tableData.length;
    }
  }, [tableData.length, selectAllActive, pageSize, table, updating]);

  useEffect(() => {
    // Only fetch once settings are loaded and column visibility is initialized
    // This prevents fetching with default/empty filters before saved view settings are applied
    if (settingsLoaded && hasInitializedColumnVisibility) {
      getTableData();
    }
  }, [pageSize, pageIndex, sorting, refetch, settingsLoaded, hasInitializedColumnVisibility]);

  useEffect(() => {
    if (!loading) {
      if (pageIndex !== 0) {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }
      const resetPage = true;
      getTableData(resetPage);
    }
  }, [searchQuery, filters, filterMatch]);

  useEffect(() => {
    if (selectAllActive && pageSize !== 1000) {
      setSelectAllActive(false);
    }
  }, [pageSize, selectAllActive]);

  // Clean up invalid row selections after data refresh (e.g., after bulk delete)
  const prevTableDataLengthForCleanupRef = useRef(tableData.length);

  useEffect(() => {
    // Only check when data has changed (not on every rowSelection change)
    const dataChanged = prevTableDataLengthForCleanupRef.current !== tableData.length;

    if (!loading && !updating && _isNotEmpty(rowSelection) && dataChanged) {
      const currentRowIds = new Set(tableData.map((_, index) => String(index)));
      const selectedRowIds = Object.keys(rowSelection);
      const hasInvalidSelections = selectedRowIds.some((id) => !currentRowIds.has(id));

      if (hasInvalidSelections) {
        // Clear selections
        setRowSelection({});
      }

      prevTableDataLengthForCleanupRef.current = tableData.length;
    }

    if (loading || updating) {
      prevTableDataLengthForCleanupRef.current = tableData.length;
    }
  }, [tableData.length, loading, updating, rowSelection]);

  // Effect 1: Trigger initial selection on table load
  useEffect(() => {
    if (shouldPerformInitialSelection(selectAllOnLoad, hasPerformedInitialSelection, loading, tableData)) {
      table.toggleAllRowsSelected(true);
      setHasPerformedInitialSelection(true);
      setNeedsInitialSelectionNotification(true);
    }
  }, [selectAllOnLoad, hasPerformedInitialSelection, loading, tableData, table]);

  // Effect 2: Notify parent after selection state has updated
  // This runs after toggleAllRowsSelected completes, ensuring fresh selection data
  useEffect(() => {
    if (shouldNotifyInitialSelection(needsInitialSelectionNotification, rowSelection)) {
      if (onSelectRow) {
        onSelectRow(table.getRowModel().rows, { pageIndex, pageSize, sortBy } as any);
      }
      setNeedsInitialSelectionNotification(false);
    }
  }, [needsInitialSelectionNotification, rowSelection, table, pageIndex, pageSize, sortBy]);

  // ================================================================
  // EVENT HANDLERS
  // ================================================================

  const handleSelectAllClick = () => {
    if (selectAllActive) {
      setSelectAllActive(false);
      setPagination((prev) => ({ ...prev, pageSize: 10 }));
      table.toggleAllRowsSelected(false);
    } else {
      setSelectAllActive(true);
      setPagination((prev) => ({ ...prev, pageSize: 1000 }));
    }
  };

  let actionBarProps: Record<string, unknown> = {
    searchQuery,
    setSearchQuery,
    filterOptions,
    filters,
    setFilters,
    filterMatch,
    setFilterMatch,
    searchPlaceholderText,
    updating,
    searchQueryBy: queryBy,
    setSearchQueryBy: setQueryBy,
    searchQueryByOptions,
    showMetricChangeFrom,
    tableName: reduxTableName,
    hideSearch,
    hideFilter,
    hasSelectorColumn,
  };

  if (bulkActions) {
    actionBarProps = {
      ...actionBarProps,
      bulkActions: bulkActions({
        useTableProps: useTableProps as never,
        setUpdating,
        setApiError,
        resetTableState,
      } as never),
      selectAllActive,
      handleSelectAllClick,
    };
  }

  const handleExportClick = async (route: string) => {
    setExporting(true);

    // Get column paths for export: prioritize accessorPath, then accessorKey, then id
    const columnPaths = columns.reduce<Array<string>>((acc, c) => {
      const path = c.accessorPath ?? (c.accessorKey as string | undefined) ?? c.id;
      if (path) {
        acc.push(path);
      }
      return acc;
    }, []);

    const params = getParams({
      searchQuery,
      filters,
      filterMatch,
      sortBy,
      pageSize,
      pageIndex,
      includeParam,
      columns: columnPaths,
    });

    try {
      const { data, headers } = await Http({
        method: 'get',
        url: getExportUrl(route, params),
        responseType: 'blob',
      });
      downloadFile({ data, headers });
      successToast(`Downloaded ${headers['file-name']}`);
    } catch (error) {
      setApiError(error);
    } finally {
      setExporting(false);
    }
  };

  // ================================================================
  // RENDER PREPARATION
  // ================================================================

  const AsideComponent = !(ExternalAsideComponent instanceof Function)
    ? (ExternalAsideComponent ?? null)
    : ExternalAsideComponent({ useTableProps: useTableProps as never });

  const hasLeftColumnContent = !hideSearch || !hideFilter || Boolean(bulkActions) || _isNotEmpty(filters);

  // ================================================================
  // COMPONENT  rendering v8-
  // ================================================================

  return (
    <>
      {loading || _isEmpty(columns) ? (
        <div className={loadingClassName}>
          <Loading txt={loadingText} />
        </div>
      ) : (
        <>
          <div
            ref={tableRef}
            className={`ui-card ${updating ? 'ui__disabled' : ''}`}
            data-test-component="APITable"
            data-test-element="api-table-container"
          >
            {Boolean(apiError) && apiError !== false && (
              <ApiErrorBanner errorType={'fetch' as never} error={apiError} onDismiss={() => setApiError(false)} />
            )}
            <TableProvider value={{ table, ...useTableProps }}>
              <div className="pa3 bb b--moon-gray flex">
                {hasLeftColumnContent && (
                  <div className="w-80">
                    <ActionBar {...(actionBarProps as any)} />
                    {_isNotEmpty(filters) && (
                      <Filters
                        filters={filters}
                        filterMatch={filterMatch}
                        filterOptions={filterOptions as Array<{ value: string | number; name: string }>}
                        updateFilters={(filters: Record<string, unknown>[], filterMatch: boolean) => {
                          setFilters(filters);
                          setFilterMatch(filterMatch);
                        }}
                      />
                    )}
                  </div>
                )}
                <div
                  className={`${hasLeftColumnContent ? 'w-20 items-end' : 'w-100'} flex flex-column justify-between`}
                >
                  <div className="flex items-start">
                    {AsideComponent}
                    {exportRoutes && (
                      <ExportButton
                        routes={exportRoutes}
                        handleExportClick={handleExportClick}
                        exporting={exporting}
                        disabled={_isEmpty(tableData)}
                      />
                    )}
                  </div>
                  {userTableViewsEnabled && showViewSelector && !renderCustomActions && (
                    <div className={AsideComponent !== null || exportRoutes ? 'mt3' : ''}>
                      <ViewSelector
                        tableName={reduxTableName as UserTableViewTableName}
                        getCurrentSettings={() => ({
                          column_visibility: filterSystemColumnsFromVisibility(columnVisibility),
                          column_sizing: columnSizing,
                          page_size: pagination.pageSize,
                          sort: sorting[0],
                          filters: filters as Array<UserTableViewFilter>,
                          match_all_filters: filterMatch,
                        })}
                        onViewChange={handleViewChange}
                      />
                    </div>
                  )}
                </div>
              </div>
              <TableWithContext tableRef={tableRef} testIdPrefix={testIdPrefix} maxHeight={maxHeight} />
            </TableProvider>
          </div>
          {renderCustomActions && (
            <div className="flex flex-column items-end">
              {renderCustomActions({
                selectedFlatRows: table.getSelectedRowModel().flatRows.map((row) => ({
                  ...row,
                  original: row.original,
                  index: row.index,
                })),
              })}
              {userTableViewsEnabled && showViewSelector && (
                <div className="mt2">
                  <ViewSelector
                    tableName={reduxTableName as UserTableViewTableName}
                    getCurrentSettings={() => ({
                      column_visibility: filterSystemColumnsFromVisibility(columnVisibility),
                      column_sizing: columnSizing,
                      page_size: pagination.pageSize,
                      sort: sorting[0],
                      filters: filters as Array<UserTableViewFilter>,
                      match_all_filters: filterMatch,
                    })}
                    onViewChange={handleViewChange}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default APITable;
